import { Controller } from '@hotwired/stimulus';

// The two cross-field rules of the "Profile and conditions" step: the seniority seeds the
// years range, and the contract type decides which pay section applies.
export default class extends Controller {
  static targets = ['minYears', 'maxYears', 'salarySection', 'rateSection'];

  static values = { experienceRanges: Object, rangeAnswered: Boolean };

  connect() {
    this.autofilled = { min: null, max: null };
    this.applyContractType(this.currentValueOf('job_ad_form[contract_type]'));

    // An import or duplicate arrives with a level but no range. Once the step has been
    // answered, empty is the answer, and seeding would put the range back on every visit.
    // The seed stays out of the draft until the employer changes something themselves.
    if (!this.rangeAnsweredValue && this.rangeEmpty()) {
      this.applyRange(this.currentValueOf('job_ad_form[experience_level]'), { silent: true });
    }
  }

  levelPicked(event) {
    this.applyRange(event.detail.selected);
  }

  contractTypePicked(event) {
    this.applyContractType(event.detail.selected);
  }

  applyRange(level, { silent = false } = {}) {
    const range = this.experienceRangesValue[String(level)];
    if (!range) return;

    this.autofilled.min = this.setYears(this.minYearsTarget, range.min, this.autofilled.min, silent);
    this.autofilled.max = this.setYears(this.maxYearsTarget, range.max, this.autofilled.max, silent);
  }

  // Returns the value the field now holds from a seed, or null once the employer owns it.
  setYears(field, years, previous, silent) {
    const select = this.selectIn(field);
    const value = String(years);

    if (this.employerOwns(select, previous)) return select.value === value ? value : null;

    const tomSelect = this.tomSelectFor(select);
    if (tomSelect) {
      tomSelect.setValue(value, silent);
    } else {
      select.value = value;
      if (!silent) select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return value;
  }

  // A value the employer typed is theirs; empty, or a previous seed, is ours to replace.
  employerOwns(select, previous) {
    return select.value !== '' && select.value !== previous;
  }

  rangeEmpty() {
    return this.selectIn(this.minYearsTarget).value === '' &&
      this.selectIn(this.maxYearsTarget).value === '';
  }

  applyContractType(contractType) {
    this.showSection(this.salarySectionTarget, contractType === 'permanent' || contractType === 'both');
    this.showSection(this.rateSectionTarget, contractType === 'contractor' || contractType === 'both');
  }

  // Hidden fields are disabled too, so pay that does not apply is never submitted.
  showSection(section, visible) {
    section.hidden = !visible;
    section
      .querySelectorAll('input, select, textarea')
      .forEach((field) => { field.disabled = !visible; });

    // TomSelect copies `disabled` once at init and never looks again, so a select hidden at
    // page load would stay locked for good.
    section.querySelectorAll('select').forEach((select) => {
      const tomSelect = this.tomSelectFor(select);
      if (!tomSelect) return;

      if (visible) tomSelect.enable(); else tomSelect.disable();
    });
  }

  selectIn(field) {
    return field.querySelector('select');
  }

  // The kit field owns the TomSelect instance; before it connects there is none, and the
  // native select is written instead.
  tomSelectFor(select) {
    const root = select.closest('[data-controller~="select-field-component"]');
    if (!root) return null;

    return this.application.getControllerForElementAndIdentifier(root, 'select-field-component')?.tomSelect;
  }

  currentValueOf(name) {
    const field = this.element.querySelector(`input[name="${name}"]:not(:disabled)`);

    return field ? field.value : null;
  }
}
